import { useI18n } from "@baton/i18n";
import { X } from "@baton/icons";
import { cva } from "class-variance-authority";
import { Dialog as Primitive } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../../cn.ts";
import { IconButton } from "../../primitives/buttons/IconButton/IconButton.tsx";
import { useScrollFade } from "../../primitives/layout/ScrollArea/scrollFade.ts";
import { useOverlayStage } from "../portalContainer.tsx";
import { scrimClassName, sheetBodyClassName, sheetHeaderClassName, sheetPanelClassName, sheetTitleClassName } from "../surfaces.ts";

/**
 * FR-015-110, § Overlays, Drawer, taken from shadcn/ui's Sheet over Radix Dialog (Spectrum UI's
 * Animated Drawer is a bottom sheet and does not describe a side drawer) and themed through the
 * variables of `packages/tokens` (2026-09-15, by the owner's decision): sliding in from the
 * inline end or start with the slow tier, and the whole screen below `--bp-md`.
 *
 * What differs from the source, and why:
 * - the sides are the logical start and end, sliding with tw-animate's logical utilities, so a
 *   right-to-left document mirrors the drawer, where the source names left and right;
 * - the width is `--dialog-width-sm`, `-md` or `-lg` from `--bp-md` up and the full width below
 *   it, where the source takes three quarters up to 24 rem;
 * - the close control sits in the header row beside the title, as the side peek's does, and is
 *   Baton's IconButton with its translated name;
 * - opening moves the focus to the panel itself, as `Dialog` does, where the source takes the first
 *   tabbable element that is not a link, which is that close button (2026-10-02, by the owner's report:
 *   "please dont show this tooltip when we first open it... a lot of pages do that"); Tab from the panel
 *   reaches the close button;
 * - controls may sit at the end of the title row before the close button, as `Dialog`'s `actions`
 *   do (2026-10-02);
 * - a press on a toast is not an interaction outside the drawer, so a toast's action, the Undo of a
 *   choice made in the drawer, acts and leaves the drawer open where the source closes it
 *   (2026-10-03, the offers sheet, where each Skip toasts its Undo).
 */
export interface DrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  /** Controls at the end of the title row, before the close button. */
  readonly actions?: ReactNode;
  readonly side?: "start" | "end";
  readonly size?: "sm" | "md" | "lg";
  readonly trigger?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}

const drawer = cva(
  cn(
    sheetPanelClassName,
    "fixed inset-y-0 z-(--layer-overlay) h-full w-full outline-hidden transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-(--duration-base) data-[state=open]:animate-in data-[state=open]:duration-(--duration-slow)",
  ),
  {
    variants: {
      side: {
        end: "end-0 md:border-s data-[state=closed]:slide-out-to-end data-[state=open]:slide-in-from-end",
        start: "start-0 md:border-e data-[state=closed]:slide-out-to-start data-[state=open]:slide-in-from-start",
      },
      size: {
        sm: "md:max-w-(--dialog-width-sm)",
        md: "md:max-w-(--dialog-width-md)",
        lg: "md:max-w-(--dialog-width-lg)",
      },
    },
  },
);

/** Puts the focus on the drawer's panel, the element Radix dispatches its open focus event on. */
function focusPanel(event: Event): void {
  event.preventDefault();
  if (event.target instanceof HTMLElement) event.target.focus();
}

/** A press or focus that lands on a toast leaves the drawer open: the toast's action belongs to what the drawer did. */
function keepOpenForToasts(event: { readonly target: EventTarget | null; preventDefault: () => void }) {
  if (event.target instanceof Element && event.target.closest('[data-slot="toast-region"]') !== null) event.preventDefault();
}

export function Drawer({ open, onOpenChange, title, actions, side = "end", size = "md", trigger, children, className }: DrawerProps) {
  const { t } = useI18n();
  // The body fades at an edge its content continues past (`scrollFade.ts`, 2026-10-21).
  const body = useScrollFade<HTMLDivElement>();
  const overlayStage = useOverlayStage();
  return (
    <Primitive.Root data-slot="sheet" open={open} onOpenChange={onOpenChange}>
      {trigger !== undefined && (
        <Primitive.Trigger data-slot="sheet-trigger" asChild>
          {trigger}
        </Primitive.Trigger>
      )}
      <Primitive.Portal data-slot="sheet-portal" {...(overlayStage === undefined ? {} : { container: overlayStage })}>
        <Primitive.Overlay data-slot="sheet-overlay" className={scrimClassName} />
        <Primitive.Content data-slot="sheet-content" className={cn(drawer({ side, size }), className)} data-side={side} data-size={size} aria-describedby={undefined} onOpenAutoFocus={focusPanel} onInteractOutside={keepOpenForToasts}>
          <div data-slot="sheet-header" className={sheetHeaderClassName}>
            <Primitive.Title data-slot="sheet-title" className={sheetTitleClassName}>
              {title}
            </Primitive.Title>
            {actions !== undefined && <span className="inline-flex flex-none items-center gap-1">{actions}</span>}
            <Primitive.Close data-slot="sheet-close" asChild>
              <IconButton icon={<X />} label={t("ui.dialog.close")} size="sm" />
            </Primitive.Close>
          </div>
          <div ref={body} data-slot="sheet-body" className={sheetBodyClassName}>
            {children}
          </div>
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}
